// src/redux/slices/siteContentSlice.ts
import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { SiteContent } from '../../types/siteContent';
import type { RootState } from '../store';
import { replaceMenuWidget } from '../../utils/widgetUtils';
import { PAGE_TYPES } from '../../utils/pageWidgetUtils';

interface SiteContentState {
  data: SiteContent | null;
  fetchLoading: boolean;
  updateLoading: boolean;
  publishLoading: boolean;
  error: string | null;
}

const initialState: SiteContentState = {
  data: null,
  fetchLoading: false,
  updateLoading: false,
  publishLoading: false,
  error: null,
};

export const siteContentSlice = createSlice({
  name: 'siteContent',
  initialState,
  reducers: {
    fetchSiteContentRequest: state => {
      state.fetchLoading = true;
      state.error = null;
    },
    fetchSiteContentSuccess: (state, action: PayloadAction<SiteContent>) => {
      state.fetchLoading = false;
      state.data = action.payload ? structuredClone(action.payload) : null;
      state.error = null;
    },
    fetchSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.fetchLoading = false;
      state.error = action.payload;
      state.data = null;
    },

    // Update widget data - Proper Immer pattern (in-place mutation)
    updateWidgetData: (state, action: PayloadAction<{ index: number; data: any }>) => {
      const { index, data } = action.payload;

      if (state.data?.widgets && state.data.widgets[index]) {
        // Mutate in place (Immer tracks this safely)
        state.data.widgets[index].data = data;

        if (state.data.homeWidgets) {
          const homeWidgetIndex = state.data.homeWidgets.findIndex(
            (widget: any) => widget.type === state.data!.widgets![index].type
          );

          if (homeWidgetIndex >= 0) {
            state.data.homeWidgets[homeWidgetIndex].data = data;
          }
        }
      }
    },

    // Add new widget section
    addWidgetSection: (
  state,
  action: PayloadAction<{ widget: any; afterIndex?: number }>
) => {
  const logPrefix = "[🧩 addWidgetSection]";

  if (!state.data?.widgets) {
    console.warn(`${logPrefix} ❌ No widgets found in state.data`);
    return;
  }

  const { widget, afterIndex } = action.payload;
  console.group(`${logPrefix} ⚙️ Add Widget Section Operation`);
  console.debug(`${logPrefix} 📦 Incoming widget:`, widget);
  console.debug(`${logPrefix} 📍 afterIndex:`, afterIndex);

  // --- Step 1: Validate and insert into main Redux widgets array
  // Handle undefined afterIndex (add at end) or valid index
  
  // Check if this is a page-specific widget using centralized PAGE_TYPES
  const isPageSpecificWidget = widget.type && PAGE_TYPES.some(pageType =>
    widget.type.includes(`_${pageType}_`)
  );
  
  if (afterIndex === undefined) {
    // No afterIndex provided - add at the end
    console.debug(`${logPrefix} 📍 No afterIndex - adding widget at the end`);
    state.data.widgets.push(widget);
    console.debug(`${logPrefix} ✅ Widget added to data.widgets at end`);
    
    // Only add to homeWidgets if it's NOT a page-specific widget
    if (state.data.homeWidgets && !isPageSpecificWidget) {
      state.data.homeWidgets.push(widget);
      console.debug(`${logPrefix} ✅ Widget added to homeWidgets at end`);
    } else if (isPageSpecificWidget) {
      console.debug(`${logPrefix} ℹ️ Page-specific widget - skipping homeWidgets`);
    }
  } else if (afterIndex >= 0) {
    console.debug(`${logPrefix} 🔢 Inserting widget after index: ${afterIndex}`);

    // Insert widget into main Redux widgets array
    state.data.widgets.splice(afterIndex + 1, 0, widget);
    console.debug(
      `${logPrefix} ✅ Widget inserted into data.widgets at position ${
        afterIndex + 1
      }`
    );
    console.debug(`${logPrefix} 🧱 Current widgets array:`, state.data.widgets);

    // --- Step 2: Update homeWidgets (the visible UI widget list)
    if (state.data.homeWidgets) {
      console.debug(`${logPrefix} 🔍 Updating homeWidgets array...`);

      // Get target widget at insertion position
      const targetWidget = state.data.widgets[afterIndex];
      const targetType = targetWidget?.type;
      console.debug(`${logPrefix} 🎯 Target widget type at afterIndex:`, targetType);

      // Find where that type appears in homeWidgets
      const homeWidgetIndex = state.data.homeWidgets.findIndex(
        (w: any) => w.type === targetType
      );
      console.debug(`${logPrefix} 📊 homeWidgetIndex found:`, homeWidgetIndex);

      // Only update homeWidgets if it's NOT a page-specific widget
      if (!isPageSpecificWidget) {
        // Insert widget into homeWidgets accordingly
        if (homeWidgetIndex >= 0) {
          state.data.homeWidgets.splice(homeWidgetIndex + 1, 0, widget);
          console.debug(
            `${logPrefix} ✅ Widget inserted into homeWidgets at position ${
              homeWidgetIndex + 1
            }`
          );
        } else {
          // Fallback: push to the end if type not found
          state.data.homeWidgets.push(widget);
          console.warn(
            `${logPrefix} ⚠️ Target type not found in homeWidgets — appended to end.`
          );
        }
      } else {
        console.debug(`${logPrefix} ℹ️ Page-specific widget - skipping homeWidgets`);
      }

      console.debug(`${logPrefix} 🧱 Updated homeWidgets array:`, state.data.homeWidgets);
    } else {
      console.warn(`${logPrefix} ⚠️ homeWidgets not found in state.data`);
    }
  } else {
    console.warn(
      `${logPrefix} ⚠️ Invalid afterIndex: ${afterIndex}. Widget insertion skipped.`
    );
  }

  console.groupEnd();
},

    // Delete widget section
    deleteWidgetSection: (state, action: PayloadAction<number>) => {
      const logPrefix = "[🗑️ deleteWidgetSection]";

      if (!state.data?.widgets || !state.data.widgets[action.payload]) {
        console.warn(`${logPrefix} ❌ Invalid widget index or no widgets found`);
        return;
      }

      const widgetIndex = action.payload;
      console.group(`${logPrefix} ⚙️ Delete Widget Section Operation`);
      console.debug(`${logPrefix} 📍 Widget index to delete:`, widgetIndex);

      // Get widget to delete
      const widgetToDelete = state.data.widgets[widgetIndex];
      const widgetType = widgetToDelete?.type;
      console.debug(`${logPrefix} 🎯 Widget type to delete:`, widgetType);
      
      // Remove from main widgets array
      state.data.widgets.splice(widgetIndex, 1);
      console.debug(`${logPrefix} ✅ Widget removed from data.widgets`);
      console.debug(`${logPrefix} 🧱 Current widgets array:`, state.data.widgets);
      
      // Check if it's a page-specific widget using centralized PAGE_TYPES
      const isPageSpecificWidget = widgetType && PAGE_TYPES.some(pageType =>
        widgetType.includes(`_${pageType}_`)
      );
      
      // Only remove from homeWidgets if it's NOT a page-specific widget
      if (state.data.homeWidgets && widgetType && !isPageSpecificWidget) {
        console.debug(`${logPrefix} 🔍 Updating homeWidgets array...`);
        
        // Find the widget with the same type in homeWidgets
        const homeWidgetIndex = state.data.homeWidgets.findIndex(
          (widget: any) => widget.type === widgetType
        );
        console.debug(`${logPrefix} 📊 homeWidgetIndex found:`, homeWidgetIndex);
        
        if (homeWidgetIndex >= 0) {
          state.data.homeWidgets.splice(homeWidgetIndex, 1);
          console.debug(`${logPrefix} ✅ Widget removed from homeWidgets at index ${homeWidgetIndex}`);
        } else {
          console.warn(`${logPrefix} ⚠️ Widget type not found in homeWidgets`);
        }
        
        console.debug(`${logPrefix} 🧱 Updated homeWidgets array:`, state.data.homeWidgets);
      } else if (isPageSpecificWidget) {
        console.debug(`${logPrefix} ℹ️ Page-specific widget - skipping homeWidgets removal`);
      } else {
        console.warn(`${logPrefix} ⚠️ homeWidgets not found or widget type missing`);
      }

      console.groupEnd();
    },

    // Update site content actions
    updateSiteContentRequest: state => {
      state.updateLoading = true;
      state.error = null;
    },
    updateSiteContentSuccess: (state, action: PayloadAction<SiteContent>) => {
      state.updateLoading = false;
      state.data = action.payload ? structuredClone(action.payload) : null;
      state.error = null;
    },
    updateSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.updateLoading = false;
      state.error = action.payload;
    },

    // Publish site content actions
    publishSiteContentRequest: state => {
      state.publishLoading = true;
      state.error = null;
    },
    publishSiteContentSuccess: (state) => {
      state.publishLoading = false;
      state.error = null;
      // Don't update state.data - let fetchSiteContentSuccess handle that
    },
    publishSiteContentFailure: (state, action: PayloadAction<string>) => {
      state.publishLoading = false;
      state.error = action.payload;
    },

    // Clear site content
    clearSiteContent: state => {
      state.data = null;
      state.error = null;
      state.fetchLoading = false;
      state.updateLoading = false;
      state.publishLoading = false;
    },

    // Manual reset for stuck loading states
    resetLoadingStates: state => {
      state.fetchLoading = false;
      state.updateLoading = false;
      state.publishLoading = false;
    },

    // Replace menu widget type while preserving data
    replaceMenuWidgetType: (state, action: PayloadAction<string>) => {
      if (state.data) {
        // Update widgets array using utility function
        if (state.data.widgets) {
          state.data.widgets = replaceMenuWidget(
            state.data.widgets,
            action.payload
          );
        }
      }
    },

  },
});

export const {
  fetchSiteContentRequest,
  fetchSiteContentSuccess,
  fetchSiteContentFailure,
  updateWidgetData,
  addWidgetSection,
  deleteWidgetSection,
  updateSiteContentRequest,
  updateSiteContentSuccess,
  updateSiteContentFailure,
  publishSiteContentRequest,
  publishSiteContentSuccess,
  publishSiteContentFailure,
  clearSiteContent,
  resetLoadingStates,
  replaceMenuWidgetType,
} = siteContentSlice.actions;

// Safe Selector (Deep clone result)
export const selectPlainSiteContent = createSelector(
  (state: RootState) => state.siteContent.data,
  (data) => data ? JSON.parse(JSON.stringify(data)) : null
);

export default siteContentSlice.reducer;
